import React from 'react';
import { motion } from 'framer-motion';
import { Trash2, Calendar, Clock, MapPin, Users, Minus, Plus } from 'lucide-react';
export interface CartItemType {
  id: string;
  type: 'table' | 'room' | 'event' | 'menu';
  title: string;
  subtitle?: string;
  date?: string;
  time?: string;
  location?: string;
  guests?: number;
  price: number;
  image?: string;
  deposit?: number;
  quantity?: number;
}
interface CartItemProps {
  item: CartItemType;
  onRemove: (id: string) => void;
  onUpdateQuantity?: (id: string, quantity: number) => void;
}
export function CartItem({
  item,
  onRemove,
  onUpdateQuantity
}: CartItemProps) {
  return <motion.div layout initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} className="group relative bg-[#1A2228] border border-white/5 rounded-xl overflow-hidden hover:border-[#DDA247]/30 transition-colors duration-300" data-id="element-141">
      <div className="flex flex-col sm:flex-row" data-id="element-142">
        {/* Image */}
        <div className="sm:w-48 h-48 sm:h-auto relative overflow-hidden bg-gray-900 flex-shrink-0" data-id="element-143">
          {item.image ? <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-144" /> : <div className="w-full h-full flex items-center justify-center text-gray-600" data-id="element-145">
              No Image
            </div>}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1014]/80 to-transparent sm:hidden" data-id="element-146" />
          <div className="absolute top-3 left-3 bg-[#DDA247] text-[#0B1014] text-xs font-bold px-2 py-1 uppercase tracking-wider rounded" data-id="element-147">
            {item.type}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 flex flex-col justify-between" data-id="element-148">
          <div className="flex justify-between items-start gap-4" data-id="element-149">
            <div data-id="element-150">
              <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-1" data-id="element-151">
                {item.title}
              </h3>
              {item.subtitle && <p className="text-[#DDA247] text-sm font-medium mb-3" data-id="element-152">
                  {item.subtitle}
                </p>}

              {item.type !== 'menu' && <div className="space-y-2 mt-4" data-id="element-153">
                  {item.date && <div className="flex items-center text-gray-400 text-sm" data-id="element-154">
                      <Calendar className="w-4 h-4 mr-2 text-[#DDA247]/70" data-id="element-155" />
                      {item.date}
                    </div>}
                  {item.time && <div className="flex items-center text-gray-400 text-sm" data-id="element-156">
                      <Clock className="w-4 h-4 mr-2 text-[#DDA247]/70" data-id="element-157" />
                      {item.time}
                    </div>}
                  {item.location && <div className="flex items-center text-gray-400 text-sm" data-id="element-158">
                      <MapPin className="w-4 h-4 mr-2 text-[#DDA247]/70" data-id="element-159" />
                      {item.location}
                    </div>}
                  {item.guests && <div className="flex items-center text-gray-400 text-sm" data-id="element-160">
                      <Users className="w-4 h-4 mr-2 text-[#DDA247]/70" data-id="element-161" />
                      {item.guests} Guests
                    </div>}
                </div>}
            </div>

            <button onClick={() => onRemove(item.id)} className="text-gray-500 hover:text-red-400 transition-colors p-2 hover:bg-white/5 rounded-full" aria-label="Remove item" data-id="element-162">
              <Trash2 className="w-5 h-5" data-id="element-163" />
            </button>
          </div>

          <div className="mt-6 pt-4 border-t border-white/5 flex justify-between items-end" data-id="element-164">
            <div className="flex items-center gap-4" data-id="element-165">
              {item.type === 'menu' && onUpdateQuantity && <div className="flex items-center gap-3 bg-white/5 rounded-lg p-1" data-id="element-166">
                  <button onClick={() => onUpdateQuantity(item.id, (item.quantity || 1) - 1)} className="p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors" data-id="element-167">
                    <Minus className="w-4 h-4" data-id="element-168" />
                  </button>
                  <span className="text-white font-medium w-4 text-center" data-id="element-169">
                    {item.quantity || 1}
                  </span>
                  <button onClick={() => onUpdateQuantity(item.id, (item.quantity || 1) + 1)} className="p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors" data-id="element-170">
                    <Plus className="w-4 h-4" data-id="element-171" />
                  </button>
                </div>}
              {item.deposit && <p className="text-xs text-gray-500 mb-1" data-id="element-172">
                  Deposit Required: ${item.deposit.toLocaleString()}
                </p>}
            </div>
            <div className="text-right" data-id="element-173">
              <p className="text-2xl font-bold text-white" data-id="element-174">
                $
                {(item.price * (item.quantity || 1)).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}